import {
    Box,
    ButtonBase,
    Divider,
    ListSubheader,
    Menu,
    Tooltip,
} from '@mui/material';
import { ChevronDown, X } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CheckboxMenuItem } from '~/components/Menu';
import {
    filterGroupSummary,
    filterOptionLabel,
    isFilterSelected,
    type PatientFilterFacetId,
    type PatientFilterGroup,
    type PatientFilters,
} from './model';

type FilterGroupChipProps = {
    group: PatientFilterGroup;
    filters: PatientFilters;
    onToggle: (facetId: PatientFilterFacetId, value: string) => void;
    onClear: (group: PatientFilterGroup) => void;
    /** Told when the chip's menu opens and closes. */
    onMenuOpenChange: (open: boolean) => void;
};

/**
 * One filter control as a chip: its name, then "name: value" or a count, opening
 * a multi-select menu of its values, headed per dimension when it holds several.
 * It stays where it is whatever is selected; a selected chip is tinted and
 * carries its own clear control.
 */
const FilterGroupChip = forwardRef<HTMLButtonElement, FilterGroupChipProps>(
    function FilterGroupChip(
        { group, filters, onToggle, onClear, onMenuOpenChange },
        ref
    ) {
        const intl = useIntl();
        const [anchor, setAnchorState] = useState<HTMLElement | null>(null);
        const setAnchor = (next: HTMLElement | null) => {
            setAnchorState(next);
            onMenuOpenChange(next !== null);
        };
        const { label, valueSummary, valueLabels } = filterGroupSummary(
            intl,
            group,
            filters
        );
        const active = valueSummary !== undefined;
        const headed = group.facets.length > 1;

        return (
            <Box
                sx={(theme) => ({
                    display: 'inline-flex',
                    alignItems: 'stretch',
                    height: 30,
                    borderRadius: 1,
                    border: `1px solid ${
                        active
                            ? theme.palette.primary.main
                            : theme.palette.divider
                    }`,
                    bgcolor: active ? 'action.selected' : 'transparent',
                    overflow: 'hidden',
                    fontSize: 13,
                })}
            >
                <Tooltip
                    title={
                        valueLabels.length > 1
                            ? intl.formatList(valueLabels)
                            : ''
                    }
                    placement="bottom-start"
                >
                    <ButtonBase
                        ref={ref}
                        onClick={(event) => setAnchor(event.currentTarget)}
                        aria-haspopup="menu"
                        aria-expanded={anchor !== null}
                        aria-label={
                            active
                                ? intl.formatMessage(
                                      { id: 'patientList.filter.change' },
                                      {
                                          facet: label,
                                          values: intl.formatList(valueLabels),
                                      }
                                  )
                                : intl.formatMessage(
                                      { id: 'patientList.filter.by' },
                                      { facet: label }
                                  )
                        }
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            font: 'inherit',
                            whiteSpace: 'nowrap',
                            color: active ? 'text.primary' : 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        {label}
                        {active && (
                            <Box component="span" sx={{ fontWeight: 600 }}>
                                {`: ${valueSummary}`}
                            </Box>
                        )}
                        <ChevronDown size={12} aria-hidden />
                    </ButtonBase>
                </Tooltip>
                {active && (
                    <>
                        <Divider orientation="vertical" flexItem />
                        <ButtonBase
                            onClick={() => onClear(group)}
                            aria-label={intl.formatMessage(
                                { id: 'patientList.filter.remove' },
                                { facet: label }
                            )}
                            sx={{
                                px: 0.75,
                                color: 'text.secondary',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    color: 'text.primary',
                                },
                            }}
                        >
                            <X size={13} />
                        </ButtonBase>
                    </>
                )}
                <Menu
                    open={anchor !== null}
                    anchorEl={anchor}
                    onClose={() => setAnchor(null)}
                    // 'menu' not 'selectedMenu': that scrolls the checked
                    // option into view and hides the ones above it.
                    variant="menu"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: { sx: { maxHeight: 480, minWidth: 200 } },
                    }}
                >
                    {group.facets.flatMap((facet, index) => [
                        ...(headed && index > 0
                            ? [<Divider key={`${facet.id}-divider`} />]
                            : []),
                        ...(headed
                            ? [
                                  <ListSubheader key={`${facet.id}-header`}>
                                      {intl.formatMessage({
                                          id: facet.labelId,
                                      })}
                                  </ListSubheader>,
                              ]
                            : []),
                        ...facet.options.map((option) => (
                            <CheckboxMenuItem
                                key={`${facet.id}-${option.value}`}
                                checked={isFilterSelected(
                                    filters,
                                    facet.id,
                                    option.value
                                )}
                                label={filterOptionLabel(intl, option)}
                                // Stays open: several values are common.
                                onClick={() => onToggle(facet.id, option.value)}
                            />
                        )),
                    ])}
                </Menu>
            </Box>
        );
    }
);

export default FilterGroupChip;
